/**
 * Rutas del panel.
 *
 * Enrutado por código y no por ficheros: con cinco rutas, generar
 * `routeTree.gen.ts` añade un paso de build y un artefacto en el repositorio a
 * cambio de nada. Cuando el panel tenga sus ocho secciones (§10) se puede
 * migrar; el cambio es mecánico.
 */

import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router';
import { configApi, platformApi } from './lib/api.ts';
import { authClient } from './lib/auth-client.ts';
import { AcceptInvitationPage } from './routes/accept-invitation.tsx';
import { ClientsPage } from './routes/clients.tsx';
import { ComposePage } from './routes/compose.tsx';
import { DashboardPage } from './routes/dashboard.tsx';
import { ForgotPasswordPage } from './routes/forgot-password.tsx';
import { OrganizationsPage } from './routes/organizations.tsx';
import { PublicationPage } from './routes/publication.tsx';
import { PublicationsPage } from './routes/publications.tsx';
import { ResetPasswordPage } from './routes/reset-password.tsx';
import { SectionsPage } from './routes/sections.tsx';
import { SignInPage } from './routes/sign-in.tsx';
import { SignUpPage } from './routes/sign-up.tsx';
import { SitesPage } from './routes/sites.tsx';

const rootRoute = createRootRoute({ component: Outlet });

/**
 * Guarda de sesión.
 *
 * No es seguridad: la seguridad está en la API, que valida la cookie en cada
 * petición. Esto solo evita enseñar un panel vacío a quien no ha entrado.
 * Cualquier comprobación en el navegador se puede saltar, y por eso el servidor
 * nunca confía en ella.
 */
async function requireSession(pathname: string) {
  const { data } = await authClient.getSession();

  if (!data?.session) {
    throw redirect({
      to: '/entrar',
      search: { redirect: pathname },
    });
  }

  return data;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    const session = await requireSession('/');

    // Sin organización activa no hay nada que enseñar: los datos de negocio
    // cuelgan siempre de una organización.
    if (!session.session.activeOrganizationId) {
      throw redirect({ to: '/organizaciones' });
    }
  },
  component: DashboardPage,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entrar',
  // Se devuelve la propiedad solo cuando existe, en vez de ponerla a
  // `undefined`: así el tipo la marca como opcional y los enlaces a /entrar no
  // tienen que pasar un `search` vacío.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === 'string' ? { redirect: search.redirect } : {},
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/registro',
  // Con el registro por invitación esta pantalla no lleva a ninguna parte: el
  // servidor rechaza el alta sin invitación. Se manda a entrar, que es donde
  // se explica cómo se consigue el acceso. Si la consulta falla se deja pasar;
  // la decisión de verdad la toma el servidor.
  beforeLoad: async () => {
    const mode = await configApi
      .get()
      .then((config) => config.registrationMode)
      .catch(() => 'open' as const);

    if (mode === 'invitation') {
      throw redirect({ to: '/entrar' });
    }
  },
  component: SignUpPage,
});

const organizationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organizaciones',
  beforeLoad: () => requireSession('/organizaciones'),
  component: OrganizationsPage,
});

/**
 * Aceptar invitación.
 *
 * No exige sesión previa a propósito: quien llega aquí viene de un correo y
 * puede no tener cuenta todavía. La pantalla se encarga de mandarle a
 * registrarse y volver.
 */
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/aceptar-invitacion/$invitationId',
  component: AcceptInvitationPage,
});

const sitesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sitios',
  beforeLoad: async () => {
    const session = await requireSession('/sitios');

    if (!session.session.activeOrganizationId) {
      throw redirect({ to: '/organizaciones' });
    }
  },
  component: SitesPage,
});

const sectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  // La pantalla se llama "Categorías" de cara al usuario, y la dirección
  // también: una URL que dice /secciones bajo un menú que dice Categorías es
  // la misma confusión que se quería quitar.
  path: '/categorias',
  beforeLoad: async () => {
    const session = await requireSession('/categorias');

    if (!session.session.activeOrganizationId) {
      throw redirect({ to: '/organizaciones' });
    }
  },
  component: SectionsPage,
});

/**
 * Guarda comun de las pantallas de negocio: sesion y organizacion activa.
 *
 * Se extrae porque repetir el mismo `beforeLoad` en cada ruta es como una acaba
 * sin el, y esa es la que ensena un panel vacio con errores de la API en la
 * consola en vez de mandar a elegir organizacion.
 */
const businessGuard = (pathname: string) => async () => {
  const session = await requireSession(pathname);

  if (!session.session.activeOrganizationId) {
    throw redirect({ to: '/organizaciones' });
  }
};

const composeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/redactar',
  beforeLoad: businessGuard('/redactar'),
  component: ComposePage,
});

const publicationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/publicaciones',
  beforeLoad: businessGuard('/publicaciones'),
  component: PublicationsPage,
});

const publicationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/publicaciones/$id',
  beforeLoad: businessGuard('/publicaciones'),
  component: function PublicationRoute() {
    const { id } = publicationRoute.useParams();
    return <PublicationPage id={id} />;
  },
});

/**
 * Clientes: solo para quien administra la plataforma.
 *
 * La guarda de aquí es de cortesía —manda al inicio a quien llegue sin permiso
 * en vez de enseñarle una pantalla de errores—. Lo que protege los datos es que
 * el servidor rechaza cada petición de esta pantalla a quien no lo es.
 */
const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clientes',
  beforeLoad: async () => {
    await requireSession('/clientes');

    const allowed = await platformApi
      .me()
      .then((result) => result.isPlatformAdmin)
      .catch(() => false);

    if (!allowed) {
      throw redirect({ to: '/' });
    }
  },
  component: ClientsPage,
});

/** La dirección antigua sigue funcionando para quien la tenga guardada. */
const legacySectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/secciones',
  beforeLoad: () => {
    throw redirect({ to: '/categorias' });
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recuperar',
  component: ForgotPasswordPage,
});

/**
 * Destino del enlace del correo de recuperación.
 *
 * El servidor añade `token` cuando el enlace es válido y `error` cuando caducó
 * o ya se usó. Las dos cosas llegan por la URL y ninguna es de fiar: el token
 * solo sirve si el servidor lo reconoce al enviarlo.
 */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/nueva-contrasena',
  validateSearch: (search: Record<string, unknown>): { token?: string; error?: string } => ({
    ...(typeof search.token === 'string' ? { token: search.token } : {}),
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
  }),
  component: function ResetPasswordRoute() {
    const { token, error } = resetPasswordRoute.useSearch();
    return <ResetPasswordPage {...(token ? { token } : {})} {...(error ? { linkError: error } : {})} />;
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  legacySectionsRoute,
  clientsRoute,
  organizationsRoute,
  sitesRoute,
  sectionsRoute,
  composeRoute,
  publicationsRoute,
  publicationRoute,
  acceptInvitationRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
